import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import RecordDetailModal from '../components/RecordDetailModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, ChevronLeft, ChevronRight, Eye, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchRecords = ({ user, onLogout }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('email');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const pageSize = 50;

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/records`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          page_size: pageSize,
          search: searchTerm,
          search_field: searchField,
        },
      });
      setRecords(response.data.records);
      setTotalPages(response.data.total_pages);
      if (response.data.records.length === 0) {
        toast.info('No records found matching your search');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const viewDetails = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="search-records">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center gap-3">
            <div className="bg-blue-50 p-3 rounded-xl">
              <Search className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-primary">Search Records</h1>
              <p className="text-muted-foreground">Find loan records with advanced filters</p>
            </div>
          </div>

          <Card className="mb-6 border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Search Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <Select value={searchField} onValueChange={setSearchField}>
                    <SelectTrigger data-testid="search-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="offer">Offer</SelectItem>
                      <SelectItem value="verification_id">Verification ID</SelectItem>
                      <SelectItem value="first_name">First Name</SelectItem>
                      <SelectItem value="last_name">Last Name</SelectItem>
                      <SelectItem value="city">City</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-7">
                  <Input
                    placeholder="Enter search term..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="h-10"
                    data-testid="search-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button onClick={handleSearch} disabled={loading} className="w-full" data-testid="search-button">
                    <Search className="mr-2 h-4 w-4" />
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50/50 backdrop-blur-sm sticky top-0 z-10 border-b">
                    <TableRow>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Phone</TableHead>
                      <TableHead className="font-semibold">Offer</TableHead>
                      <TableHead className="font-semibold">Agent</TableHead>
                      <TableHead className="font-semibold">City</TableHead>
                      <TableHead className="font-semibold">State</TableHead>
                      <TableHead className="font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!searched ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                          Enter search criteria and click Search to find records
                        </TableCell>
                      </TableRow>
                    ) : loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Searching...
                        </TableCell>
                      </TableRow>
                    ) : records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((record, index) => (
                        <TableRow key={record.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`search-result-${index}`}>
                          <TableCell className="font-mono text-xs">{record.date}</TableCell>
                          <TableCell className="font-medium">
                            {record.first_name} {record.last_name}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{record.email}</TableCell>
                          <TableCell className="font-mono text-xs">{record.phone}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              {record.offer}
                            </span>
                          </TableCell>
                          <TableCell>{record.agent}</TableCell>
                          <TableCell>{record.city}</TableCell>
                          <TableCell>{record.state}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewDetails(record)}
                              data-testid={`view-search-detail-${index}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {searched && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPage(page - 1);
                    handleSearch();
                  }}
                  disabled={page === 1}
                  data-testid="prev-search-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPage(page + 1);
                    handleSearch();
                  }}
                  disabled={page === totalPages}
                  data-testid="next-search-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </main>
      <RecordDetailModal record={selectedRecord} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </div>
  );
};

export default SearchRecords;